import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { API_URL, BASE_URL } from "../Config";
import config from "../common/Config";
import axios from "axios";
import { toast } from "sonner";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Iconify } from "src/index";
import "../common/commonStyles.css";

const FALLBACK_IMAGE = "https://placehold.co/600x400?text=No+Image+Available";

const SliderSettings = () => {
  const [data, setData] = useState([]);
  const [sliderListUrl, setSliderListUrl] = useState(null);
  const [previews, setPreviews] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [promotions, setPromotions] = useState([]);
  const [selectedPromotions, setSelectedPromotions] = useState({});
  const fileInputRefs = useRef({});
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");

  const handleFileChange = (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file only.");
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews((prev) => ({
        ...prev,
        [index]: reader.result,
      }));
      setImageErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (index) => {
    const fileInput = fileInputRefs.current[`img${index + 1}`];
    if (fileInput) {
      fileInput.value = "";
    }
    setPreviews((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setImageErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleImageError = (index) => {
    setImageErrors((prev) => ({
      ...prev,
      [index]: true,
    }));
  };

  const handlePromotionChange = (index, promotionCode) => {
    setSelectedPromotions((prev) => ({
      ...prev,
      [index]: promotionCode,
    }));
  };

  const handleUpdateSetting = async () => {
    const formData = new FormData();
    for (let i = 0; i < 5; i++) {
      const fileInput = fileInputRefs.current[`img${i + 1}`];
      if (fileInput?.files?.[0]) {
        formData.append(`img${i + 1}`, fileInput.files[0]);
      }
      formData.append(`promotion_id${i + 1}`, selectedPromotions[i] || "");
    }
    try {
      const response = await axios.post(`${API_URL}/ws-add-slider`, formData, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        toast.success("Slider Images updated successfully.");
        getSliderSettings();
        setPreviews({});
        setImageErrors({});
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      toast.error("Slider Images update failed. Try Again");
    }
  };

  const getSliderSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/ws-get-slider`, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      });
      const row = response.data.data?.[0];
      setSliderListUrl(row?.url ?? null);
      const sliderData = row?.slider || [];
      setData(sliderData);
      const initialPromotions = {};
      sliderData.forEach((item, index) => {
        if (item?.promo_id) {
          initialPromotions[index] = item.promo_id;
        }
      });
      setSelectedPromotions(initialPromotions);
    } catch (error) {
      toast.error("Failed to fetch slider settings.");
    }
  };

  const getPromotions = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/ws-get-promotions-dropdown`,
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.data.success) {
        setPromotions(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to fetch promotions.");
    }
  };

  useEffect(() => {
    getSliderSettings();
    getPromotions();
  }, []);

  return (
    <Box className="page-container">
        <Paper className="paper-style">
          <Box
            sx={{
              px: 2,
              py: 1.2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography className="page-title">Slider Settings</Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 0.5, color: "red", fontSize: "0.8rem" }}
              >
                * Recommended size: 1600 × 700 px (16:7)
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/promo-flipbook"
              variant="contained"
              className="button-variant-contained"
              startIcon={<Iconify icon="mdi:book-open-page-variant" width={20} />}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Flipbook
            </Button>
          </Box>

          <Box sx={{ px: 3 }}>
            <Grid container spacing={2}>
              {[...Array(5)].map((_, index) => {
                const serverImage = data[index]?.image
                  ? sliderListUrl == null
                    ? `${String(BASE_URL).replace(/\/$/, "")}/images/${data[index].image}`
                    : `${String(API_URL).replace(/\/$/, "")}/public/${config.PREFIX}/images/${data[index].image}`
                  : null;

                const imageSrc = previews[index]
                  ? previews[index]
                  : serverImage && !imageErrors[index]
                    ? serverImage
                    : FALLBACK_IMAGE;
                const currentImage = previews[index] || serverImage;
                return (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box
                      sx={{
                        height: "100%",
                        border: "1px solid rgba(184, 191, 197, 0.16)",
                        borderRadius: "4px",
                        p: 2,
                        backgroundColor: "#fff",
                        boxShadow: "0 1px 1px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#212b36",
                          fontSize: "0.875rem",
                          mb: 0.5,
                        }}
                      >
                        Image {index + 1}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                          <Button
                            variant="outlined"
                            component="label"
                            size="medium"
                            fullWidth
                            startIcon={
                              <Iconify icon="eva:file-outline" width={20} />
                            }
                            sx={{
                              py: 1.2,
                              textTransform: "none",
                              justifyContent: "flex-start",
                              borderColor: "rgba(5, 5, 5, 0.54)",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Choose Banner Image
                            </Typography>
                            <input
                              ref={(el) => {
                                fileInputRefs.current[`img${index + 1}`] = el;
                              }}
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => handleFileChange(index, e)}
                            />
                          </Button>
                        </Grid>

                        <Grid item xs={12}>
                          {currentImage && (
                            <Box
                              sx={{
                                width: "100%",
                                minHeight: 120,
                                backgroundColor: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                overflow: "hidden",
                                p: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  maxHeight: 120,
                                  lineHeight: 0,
                                }}
                              >
                                {previews[index] && (
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveImage(index);
                                    }}
                                    sx={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      zIndex: 5,
                                      backgroundColor: "#fff",
                                      color: "#637381",
                                      width: 32,
                                      height: 32,
                                      boxShadow:
                                        "0 2px 8px rgba(0, 0, 0, 0.15)",
                                      border:
                                        "1px solid rgba(145, 158, 171, 0.16)",
                                      "&:hover": {
                                        backgroundColor: "#f5f7fa",
                                        color: "#212b36",
                                      },
                                    }}
                                  >
                                    <Iconify icon="eva:close-fill" width={18} />
                                  </IconButton>
                                )}
                                <Box
                                  component="img"
                                  src={imageSrc}
                                  alt="Slider preview"
                                  onError={() => handleImageError(index)}
                                  sx={{
                                    display: "block",
                                    maxWidth: "100%",
                                    maxHeight: 120,
                                    width: "auto",
                                    height: "auto",
                                    objectFit: "contain",
                                    borderRadius: 1,
                                  }}
                                />
                              </Box>
                            </Box>
                          )}

                          <Autocomplete
                            fullWidth
                            size="small"
                            options={promotions}
                            value={
                              promotions.find(
                                (promo) =>
                                  String(promo.promotion_id) ===
                                  String(selectedPromotions[index] || ""),
                              ) || null
                            }
                            onChange={(_, promo) =>
                              handlePromotionChange(
                                index,
                                promo?.promotion_id || "",
                              )
                            }
                            getOptionLabel={(promo) =>
                              `${promo.promotion_code} - ${promo.promotion_title}`
                            }
                            isOptionEqualToValue={(option, value) =>
                              String(option.promotion_id) ===
                              String(value.promotion_id)
                            }
                            noOptionsText={
                              promotions.length === 0
                                ? "None"
                                : "No promotions found"
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Promotion"
                                placeholder="Search promotion..."
                                InputLabelProps={{ sx: { fontSize: "0.875rem" } }}
                                inputProps={{
                                  ...params.inputProps,
                                  sx: { fontSize: "0.875rem" },
                                }}
                              />
                            )}
                            ListboxProps={{ style: { maxHeight: 280 } }}
                            sx={{ mt: 2 }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid rgba(145, 158, 171, 0.16)",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                className="button-variant-contained"
                onClick={handleUpdateSetting}
                startIcon={
                  <Iconify icon="eva:checkmark-circle-2-fill" width={20} />
                }
                sx={{
                  width: { xs: "100%", md: "auto" },
                }}
              >
                Update
              </Button>
            </Box>
          </Box>
        </Paper>
    </Box>
  );
};

export default SliderSettings;
